import React, { useEffect, useState, useCallback } from "react";
import { Form, message, Button, Space } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import AppInput from "../../../components/common/AppInput";
import AppButton from "../../../components/common/AppButton";
import AntdModal from "../../../components/common/AntdModal";
import { isEmpty, map } from "lodash";
import { inputNumberFormatter } from "../../../config/constants";
import usePayrollStore from "../../../config/stores/administration/usePayrollStore";
import useStaffContractsStore from "../../../config/stores/administration/useStaffContractStore";
import dayjs from "dayjs";
import axios from "axios";

const inputNumberParser = (value) => {
  return value ? value.replace(/\$\s?|(,*)/g, '') : '';
};

const monthOptions = [
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 },
];

const borderless = { variant: "borderless" };

const PayrollForm = () => {
  const [form] = Form.useForm();
  const [userProjects, setUserProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [staffList, setStaffList] = useState([]); // All staff members from /payroll/staff-list or create-or-load
  const [loadingStaffList, setLoadingStaffList] = useState(false);
  
  const payrollStore = usePayrollStore();
  const staffContractStore = useStaffContractsStore();
  const {
    showCreatePayroll,
    setCreatingPayroll,
    creating,
    creatingPayroll,
    updatingPayroll,
    payrollToEdit,
    getAllPayrolls,
    createMonthlyPayroll,
    updateMonthlyPayroll,
    currentPayrollPeriod,
  } = payrollStore;
  const { metaData, gettingMetaData } = staffContractStore;

  const selectedMonth = Form.useWatch("month", form);
  const selectedYear = Form.useWatch("year", form);
  const selectedUserId = Form.useWatch("user_id", form);

  useEffect(() => {
    if (creating && (selectedMonth || selectedYear)) {
      form.setFieldsValue({ user_id: undefined, project_allocations: [] });
      setUserProjects([]);
    }
  }, [selectedMonth, selectedYear, creating, form]);

  const loadStaffList = async (monthValue, yearValue) => {
    setLoadingStaffList(true);
    try {
      let monthInt;
      if (dayjs.isDayjs(monthValue)) {
        monthInt = monthValue.month() + 1;
      } else {
        monthInt = parseInt(monthValue);
      }
      
      let yearInt;
      if (dayjs.isDayjs(yearValue)) {
        yearInt = yearValue.year();
      } else if (typeof yearValue === 'number') {
        yearInt = yearValue;
      } else {
        yearInt = parseInt(yearValue);
      }
      
      if (isNaN(monthInt) || monthInt < 1 || monthInt > 12 || isNaN(yearInt)) {
        setLoadingStaffList(false);
        return;
      }

      const res = await axios.post(`/payroll/create-or-load`, {
        month: monthInt,
        year: yearInt,
      }, {
        skipNotification: true,
      });
      
      const responseData = res?.data || res;
      const data = responseData?.data || responseData;
      const staff = data?.staff || data?.staffList || [];
      
      setStaffList(Array.isArray(staff) ? staff : []);
    } catch (error) {
      setStaffList([]);
      message.error("Failed to load staff list. Please check month and year selection.");
    } finally {
      setLoadingStaffList(false);
    }
  };

  useEffect(() => {
    if (showCreatePayroll && creating) {
      if (selectedMonth && selectedYear) {
        loadStaffList(selectedMonth, selectedYear);
      } else if (currentPayrollPeriod && currentPayrollPeriod.month && currentPayrollPeriod.year) {
        loadStaffList(currentPayrollPeriod.month, currentPayrollPeriod.year);
      } else {
        setStaffList([]);
      }
    }
  }, [showCreatePayroll, creating, selectedMonth, selectedYear, currentPayrollPeriod]);

  useEffect(() => {
    if (creating) {
      form.resetFields();
      setUserProjects([]);
      setStaffList([]);
      
      if (currentPayrollPeriod) {
        const { month, year } = currentPayrollPeriod;
        const yearDayjs = dayjs().year(year).startOf('year');
        form.setFieldsValue({ 
          month: month,
          year: yearDayjs,
        });
        setTimeout(() => {
          loadStaffList(month, year);
        }, 100);
      } else {
        form.setFieldsValue({ 
          month: undefined,
          year: undefined,
          project_allocations: [] 
        });
      }
    } else if (payrollToEdit) {
      const yearDate = payrollToEdit.year ? dayjs(`${payrollToEdit.year}-01-01`) : null;
      
      const projectAllocationsArray = [];
      
      if (payrollToEdit.projectAllocations && typeof payrollToEdit.projectAllocations === 'object') {
        if (Array.isArray(payrollToEdit.projectAllocations)) {
          projectAllocationsArray.push(...payrollToEdit.projectAllocations.map(alloc => ({
            project_id: alloc.project_id || alloc.projectId,
            amount: alloc.amount || 0,
          })));
        } else {
          Object.keys(payrollToEdit.projectAllocations).forEach(projectId => {
            const alloc = payrollToEdit.projectAllocations[projectId];
            const amount = typeof alloc === 'object' ? alloc.amount : alloc;
            if (projectId && (amount !== null && amount !== undefined)) {
              projectAllocationsArray.push({
                project_id: projectId,
                amount: Number(amount) || 0,
              });
            }
          });
        }
      }
      
      let userId = payrollToEdit.user_id || 
                    payrollToEdit.userId || 
                    payrollToEdit.user?.id ||
                    null;
      
      if (!userId && payrollToEdit.name && staffList.length > 0) {
        const matchedStaff = staffList.find(staff => 
          staff.name === payrollToEdit.name || 
          staff.name?.toLowerCase() === payrollToEdit.name?.toLowerCase()
        );
        if (matchedStaff) {
          userId = matchedStaff.id;
        }
      }
      
      const formValues = {
        user_id: userId,
        month: payrollToEdit.month,
        year: yearDate,
        project_allocations: projectAllocationsArray.length > 0 ? projectAllocationsArray : [{ project_id: undefined, amount: undefined }],
        salary_advance: payrollToEdit.salaryAdvance || payrollToEdit.salary_advance || 0,
        staff_savings_scheme: payrollToEdit.staffSavingsScheme || payrollToEdit.staff_savings_scheme || 0,
        local_service_tax: payrollToEdit.localServiceTax || payrollToEdit.local_service_tax || 0,
        transport_allowance: payrollToEdit.transportAllowance || payrollToEdit.transport_allowance || 0,
        housing_allowance: payrollToEdit.housingAllowance || payrollToEdit.housing_allowance || 0,
        medical_allowances: payrollToEdit.medicalAllowances || payrollToEdit.medical_allowances || 0,
        other_allowances: payrollToEdit.otherAllowances || payrollToEdit.other_allowances || 0,
      };
      
      form.setFieldsValue(formValues);
      
      if (userId) {
        // Fetch user projects immediately when editing
        const fetchUserProjectsForEdit = async () => {
          setLoadingProjects(true);
          try {
            const res = await axios.get(`/payroll/user-projects`, {
              params: { user_id: userId },
            });
            
            console.log("User projects API response (edit mode):", res);
            
            const responseData = res?.data || res;
            const user = responseData?.user || null;
            const projects = responseData?.projects || [];
            
            console.log("Extracted user info (edit mode):", user);
            console.log("Extracted projects (edit mode):", projects);
            
            setUserProjects(Array.isArray(projects) ? projects : []);
            
            // After user projects are loaded, ensure project allocations are set
            // This is important because Form.List needs the values to render
            if (payrollToEdit && !creating && projects.length > 0) {
              // Always re-set project allocations after user projects load to ensure Form.List renders
              const projectAllocationsArray = [];
              if (payrollToEdit.projectAllocations && typeof payrollToEdit.projectAllocations === 'object') {
                if (Array.isArray(payrollToEdit.projectAllocations)) {
                  projectAllocationsArray.push(...payrollToEdit.projectAllocations.map(alloc => ({
                    project_id: alloc.project_id || alloc.projectId,
                    amount: alloc.amount || 0,
                  })));
                } else {
                  Object.keys(payrollToEdit.projectAllocations).forEach(projectId => {
                    const alloc = payrollToEdit.projectAllocations[projectId];
                    const amount = typeof alloc === 'object' ? alloc.amount : alloc;
                    if (projectId && (amount !== null && amount !== undefined)) {
                      projectAllocationsArray.push({
                        project_id: projectId,
                        amount: Number(amount) || 0,
                      });
                    }
                  });
                }
              }
              
              console.log("Re-setting project allocations after user projects loaded:", projectAllocationsArray);
              
              // Use setTimeout to ensure form is ready
              setTimeout(() => {
                const currentAllocations = form.getFieldValue("project_allocations");
                console.log("Current allocations in form:", currentAllocations);
                
                if (projectAllocationsArray.length > 0) {
                  form.setFieldsValue({
                    project_allocations: projectAllocationsArray,
                  });
                  console.log("Set project allocations to:", projectAllocationsArray);
                  console.log("Form values after setting:", form.getFieldsValue("project_allocations"));
                } else {
                  // If no allocations, set at least one empty row
                  form.setFieldsValue({
                    project_allocations: [{ project_id: undefined, amount: undefined }],
                  });
                }
              }, 100);
            }
          } catch (error) {
            console.error("Error fetching user projects (edit mode):", error);
            setUserProjects([]);
          } finally {
            setLoadingProjects(false);
          }
        };
        
        fetchUserProjectsForEdit();
      }
    }
  }, [creating, payrollToEdit, form]);

  useEffect(() => {
    if (!creating && payrollToEdit && staffList.length > 0) {
      let userId = payrollToEdit.user_id || 
                    payrollToEdit.userId || 
                    payrollToEdit.user?.id ||
                    null;
      
      if (!userId && payrollToEdit.name) {
        const matchedStaff = staffList.find(staff => 
          staff.name === payrollToEdit.name || 
          staff.name?.toLowerCase() === payrollToEdit.name?.toLowerCase()
        );
        if (matchedStaff) {
          userId = matchedStaff.id;
        }
      }
      
      if (userId) {
        const currentUserId = form.getFieldValue("user_id");
        if (currentUserId !== userId) {
          form.setFieldsValue({ user_id: userId });
          
          if (!userProjects.length && !loadingProjects) {
            const fetchUserProjectsForEdit = async () => {
              setLoadingProjects(true);
              try {
                const res = await axios.get(`/payroll/user-projects`, {
                  params: { user_id: userId },
                });
                
                const responseData = res?.data || res;
                const projects = responseData?.projects || [];
                
                setUserProjects(Array.isArray(projects) ? projects : []);
              } catch (error) {
                setUserProjects([]);
              } finally {
                setLoadingProjects(false);
              }
            };
            
            fetchUserProjectsForEdit();
          }
        }
      }
    }
  }, [staffList, payrollToEdit, creating, form, userProjects.length, loadingProjects]);

  useEffect(() => {
    if (creating && selectedUserId) {
      form.setFieldsValue({ project_allocations: [] });
      setUserProjects([]);
    }
  }, [selectedUserId, creating, form]);

  useEffect(() => {
    const fetchUserProjects = async () => {
      if (selectedUserId) {
        setLoadingProjects(true);
        try {
          const res = await axios.get(`/payroll/user-projects`, {
            params: { user_id: selectedUserId },
          });
          
          const responseData = res?.data || res;
          const user = responseData?.user || null;
          const projects = responseData?.projects || [];
          
          setUserProjects(Array.isArray(projects) ? projects : []);
          
          if (creating && projects && projects.length > 0) {
            const currentAllocations = form.getFieldValue("project_allocations") || [];
            if (currentAllocations.length === 0 || !currentAllocations[0]?.project_id) {
              form.setFieldsValue({
                project_allocations: [{ project_id: projects[0].id, amount: undefined }],
              });
            }
          }
        } catch (error) {
          setUserProjects([]);
        } finally {
          setLoadingProjects(false);
        }
      } else {
        setUserProjects([]);
        form.setFieldsValue({ project_allocations: [] });
      }
    };
    
    fetchUserProjects();
  }, [selectedUserId, form, creating]);


  const closeModal = () => {
    setCreatingPayroll(false, false, null);
    form.resetFields();
    setUserProjects([]);
  };

  const onSubmit = () => {
    form
      .validateFields()
      .then(async (values) => {
        const month = values.month;
        const year = values.year ? dayjs(values.year).year() : undefined;

        if (!month || !year) {
          message.error("Please select month and year before adding payroll");
          return;
        }
        
        const monthInt = parseInt(month);
        const yearInt = parseInt(year);

        if (isNaN(monthInt) || monthInt < 1 || monthInt > 12) {
          message.error("Please select a valid month (1-12)");
          return;
        }

        if (isNaN(yearInt) || yearInt < 2000 || yearInt > 2100) {
          message.error("Please select a valid year (2000-2100)");
          return;
        }
        
        const validAllocations = (values.project_allocations || []).filter(
          alloc => alloc.project_id && (alloc.amount > 0)
        );

        if (validAllocations.length === 0) {
          message.error("Please add at least one project allocation with an amount");
          return;
        }

        if (creating) {
          const payload = {
            user_id: values.user_id,
            month: monthInt,
            year: yearInt,
            project_allocations: validAllocations.map(alloc => ({
              project_id: alloc.project_id,
              amount: Number(alloc.amount) || 0,
            })),
            salary_advance: parseFloat(values.salary_advance) || 0,
            staff_savings_scheme: parseFloat(values.staff_savings_scheme) || 0,
            local_service_tax: parseFloat(values.local_service_tax) || 0,
            transport_allowance: parseFloat(values.transport_allowance) || 0,
            housing_allowance: parseFloat(values.housing_allowance) || 0,
            medical_allowances: parseFloat(values.medical_allowances) || 0,
            other_allowances: parseFloat(values.other_allowances) || 0,
          };

          await createMonthlyPayroll(payload);
          if (isEmpty(payrollStore.creatingPayrollError)) {
            closeModal();
            setTimeout(() => {
              getAllPayrolls();
            }, 100);
          } else {
            const errorMsg = payrollStore.creatingPayrollError?.response?.data?.message || 
                            payrollStore.creatingPayrollError?.message || 
                            "Failed to create payroll entry";
            message.error(errorMsg);
          }
        } else {
          const payload = {
            project_allocations: validAllocations.map(alloc => ({
              project_id: alloc.project_id,
              amount: Number(alloc.amount) || 0,
            })),
            salary_advance: parseFloat(values.salary_advance) || 0,
            staff_savings_scheme: parseFloat(values.staff_savings_scheme) || 0,
            local_service_tax: parseFloat(values.local_service_tax) || 0,
            transport_allowance: parseFloat(values.transport_allowance) || 0,
            housing_allowance: parseFloat(values.housing_allowance) || 0,
            medical_allowances: parseFloat(values.medical_allowances) || 0,
            other_allowances: parseFloat(values.other_allowances) || 0,
          };

          try {
            await updateMonthlyPayroll(payrollToEdit.id, payload);
            
            message.success("Payroll entry updated successfully");
            closeModal();
            setTimeout(() => {
              getAllPayrolls();
            }, 100);
          } catch (error) {
            const errorMsg = error?.response?.data?.message || 
                            error?.message || 
                            payrollStore.updatingPayrollError?.response?.data?.message ||
                            payrollStore.updatingPayrollError?.message ||
                            "Failed to update payroll entry";
            message.error(errorMsg);
          }
        }
      })
      .catch((error) => {
        message.error("Please fill in all required fields correctly");
      });
  };

  return (
    <AntdModal
      title={creating ? "ADD PAYROLL" : "EDIT PAYROLL"}
      open={showCreatePayroll}
      closeModal={closeModal}
      customSize={1200}
      footer={[
        <AppButton
          key="save"
          text={creating ? "CREATE" : "UPDATE"}
          loading={creatingPayroll || updatingPayroll}
          onClick={onSubmit}
        />,
        <AppButton key="cancel" text="CANCEL" onClick={closeModal} danger />,
      ]}
    >
      <Form layout="vertical" form={form}>
        <div className="p-2">
          <table className="table table-bordered table-sm">
            <tbody>
              <tr>
                <td className="fw-bold">Month</td>
                <td>
                  <AppInput
                    label={null}
                    name="month"
                    type="select"
                    rules={[{ required: true, message: "Month is required" }]}
                    options={monthOptions}
                    inputAttributes={{
                      ...borderless,
                      style: { width: "100%" },
                      placeholder: "Select month",
                      disabled: !creating || !!currentPayrollPeriod, // Disable when editing or when period is pre-selected
                    }}
                  />
                </td>
                <td className="fw-bold">Year</td>
                <td>
                  <AppInput
                    label={null}
                    name="year"
                    type="date"
                    rules={[{ required: true, message: "Year is required" }]}
                    inputAttributes={{
                      ...borderless,
                      style: { width: "100%" },
                      picker: "year",
                      format: "YYYY",
                      placeholder: "Select year",
                      disabled: !creating || !!currentPayrollPeriod, // Disable when editing or when period is pre-selected
                    }}
                  />
                </td>
              </tr>
              {/* Only show staff and other fields after month and year are selected (creating mode) */}
              {creating && selectedMonth && selectedYear ? (
                <tr>
                  <td className="fw-bold" style={{ fontWeight: 'bold' }}>Staff Name</td>
                  <td colSpan={3}>
                    <AppInput
                      label={null}
                      name="user_id"
                      type="select"
                      rules={[{ required: true, message: "Staff is required" }]}
                      options={staffList.length > 0 
                        ? map(staffList, (staff) => ({
                            label: staff.name || `${staff.first_name || ''} ${staff.other_names || ''}`.trim(),
                            value: staff.id,
                          }))
                        : []
                      }
                      inputAttributes={{
                        ...borderless,
                        loading: loadingStaffList,
                        placeholder: loadingStaffList ? "Loading staff..." : "Select staff",
                        showSearch: true,
                        disabled: loadingStaffList || staffList.length === 0,
                        style: {
                          fontWeight: loadingStaffList || staffList.length === 0 ? 'normal' : '500',
                        },
                        className: "select-staff-placeholder",
                        filterOption: (input, option) => {
                          const label = option?.label || '';
                          return label.toLowerCase().includes(input.toLowerCase());
                        },
                      }}
                    />
                    {!loadingStaffList && staffList.length === 0 && selectedMonth && selectedYear && (
                      <div className="text-muted small mt-1">
                        No staff found for this month/year. Please check your selection.
                      </div>
                    )}
                  </td>
                </tr>
              ) : !creating ? (
                <tr>
                  <td className="fw-bold" style={{ fontWeight: 'bold' }}>Staff Name</td>
                  <td colSpan={3}>
                    <AppInput
                      label={null}
                      name="user_id"
                      type="select"
                      rules={[{ required: true, message: "Staff is required" }]}
                      options={staffList.length > 0 
                        ? map(staffList, (staff) => ({
                            label: staff.name || `${staff.first_name || ''} ${staff.other_names || ''}`.trim(),
                            value: staff.id,
                          }))
                        : map(metaData?.users || [], (user) => ({
                            label: `${user.first_name} ${user.other_names}`,
                            value: user.id,
                          }))
                      }
                      inputAttributes={{
                        ...borderless,
                        loading: loadingStaffList || gettingMetaData,
                        placeholder: "Select staff",
                        showSearch: true,
                        disabled: true, // Disable when editing (user_id cannot be changed)
                        style: {
                          fontWeight: '500',
                        },
                        className: "select-staff-placeholder",
                        filterOption: (input, option) => {
                          const label = option?.label || '';
                          return label.toLowerCase().includes(input.toLowerCase());
                        },
                      }}
                    />
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={4} className="text-center text-muted p-2">
                    Please select Month and Year first to load staff list
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {((creating && selectedMonth && selectedYear && selectedUserId) || (!creating && payrollToEdit)) && (
          <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Project Allocations</strong>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => {
                  return (
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        const current = getFieldValue("project_allocations") || [];
                        form.setFieldsValue({
                          project_allocations: [...current, { project_id: undefined, amount: undefined }],
                        });
                      }}
                      disabled={!selectedUserId || loadingProjects || userProjects.length === 0}
                    >
                      Add Project
                    </Button>
                  );
                }}
              </Form.Item>
            </div>
            <Form.List name="project_allocations">
              {(fields, { add, remove }) => {
                return (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div key={key} className="mb-2 p-2 border rounded">
                      <Space style={{ width: "100%", justifyContent: "space-between" }} align="baseline">
                        <div style={{ flex: 1, display: "flex", gap: "16px", alignItems: "flex-start" }}>
                          <div style={{ flex: 1 }}>
                            <Form.Item
                              {...restField}
                              name={[name, "project_id"]}
                              rules={[{ required: true, message: "Project is required" }]}
                            >
                              <AppInput
                                label="Project"
                                name={[name, "project_id"]}
                                type="select"
                                options={map(userProjects, (project) => ({
                                  label: project.name,
                                  value: project.id,
                                }))}
                                inputAttributes={{ 
                                  placeholder: "Select project",
                                  disabled: !selectedUserId || loadingProjects || userProjects.length === 0,
                                  loading: loadingProjects,
                                }}
                              />
                            </Form.Item>
                          </div>
                          <div style={{ flex: 1 }}>
                            <Form.Item
                              {...restField}
                              name={[name, "amount"]}
                              rules={[
                                { required: true, message: "Amount is required" },
                                { type: "number", min: 0, message: "Amount must be positive" },
                              ]}
                            >
                              <AppInput
                                label="Amount"
                                name={[name, "amount"]}
                                type="number"
                                inputAttributes={{
                                  placeholder: "Enter amount",
                                  formatter: inputNumberFormatter,
                                  parser: inputNumberParser,
                                  min: 0,
                                  controls: false, // Hide spinner controls for cleaner input
                                }}
                              />
                            </Form.Item>
                          </div>
                        </div>
                        {fields.length > 1 && (
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                          />
                        )}
                      </Space>
                    </div>
                  ))}
                  {fields.length === 0 && (
                    <div className="text-center text-muted p-3 border rounded">
                      {!selectedUserId 
                        ? "Please select a staff member first"
                        : loadingProjects 
                        ? "Loading projects..."
                        : userProjects.length === 0
                        ? "No projects found for this staff member"
                        : "Click 'Add Project' to add project allocations"}
                    </div>
                  )}
                </>
                );
              }}
            </Form.List>
          </div>
          )}

          {/* Allowances Section - Only show after staff is selected */}
          {((creating && selectedMonth && selectedYear && selectedUserId) || (!creating && payrollToEdit)) && (
          <div className="mt-4">
            <h6 className="fw-bold mb-3">Allowances</h6>
            <table className="table table-bordered table-sm">
              <tbody>
                <tr>
                  <td className="fw-bold" style={{ width: "200px" }}>Transport Allowance</td>
                  <td>
                    <AppInput
                      label={null}
                      name="transport_allowance"
                      type="number"
                      inputAttributes={{
                        ...borderless,
                        placeholder: "Enter amount",
                        formatter: inputNumberFormatter,
                        parser: inputNumberParser,
                        min: 0,
                        controls: false,
                      }}
                    />
                  </td>
                  <td className="fw-bold" style={{ width: "200px" }}>Housing Allowance</td>
                  <td>
                    <AppInput
                      label={null}
                      name="housing_allowance"
                      type="number"
                      inputAttributes={{
                        ...borderless,
                        placeholder: "Enter amount",
                        formatter: inputNumberFormatter,
                        parser: inputNumberParser,
                        min: 0,
                        controls: false,
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="fw-bold">Medical Allowances</td>
                  <td>
                    <AppInput
                      label={null}
                      name="medical_allowances"
                      type="number"
                      inputAttributes={{
                        ...borderless,
                        placeholder: "Enter amount",
                        formatter: inputNumberFormatter,
                        parser: inputNumberParser,
                        min: 0,
                        controls: false,
                      }}
                    />
                  </td>
                  <td className="fw-bold">Other Allowances</td>
                  <td>
                    <AppInput
                      label={null}
                      name="other_allowances"
                      type="number"
                      inputAttributes={{
                        ...borderless,
                        placeholder: "Enter amount",
                        formatter: inputNumberFormatter,
                        parser: inputNumberParser,
                        min: 0,
                        controls: false,
                      }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          )}
        </div>
      </Form>
    </AntdModal>
  );
};

export default PayrollForm;
